import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitRating, fetchMyRating } from './communityApi';
import useAuth from './useAuth';
import './App.css';

// The human-facing description of a Hub product (Phase HUB-PAGE, 2026-09-27).
// `ProductInfo` renders in two places:
//   • the info sheet over a running web app, at /hub/<slug>/info
//   • the whole page for a product that doesn't run in a browser (a walk, a
//     prompt, media, a Mac app), at /hub/<slug>
// It reads top to bottom as a person would ask: what is it, how do I get it,
// how was it made, what do others think. Build internals sit in a collapsed
// "Technical details" section. The build record stays behind sign-in, as the
// HUB-GATE funnel (2026-07-05) decided.
//
// Before 2026-09-27 this file was EndProductDetailPage.js, the page at
// /hub/<slug>; `git log --follow` carries that history.

// Single inline icon — Share is the only chrome icon that survived the
// trim. Heroicons-style line glyph, monochrome, sized via fontSize on the
// parent button so the line weight scales with text.
const ShareIcon = (props) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 16V4M8 8l4-4 4 4" /><path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
  </svg>
);

const ANDROID_PACKAGE = 'com.sevenhillsstudio.mchatai';
const PLAY_STORE_LINK = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const APP_STORE_LINK = 'https://apps.apple.com/us/app/mchatai/id6445939409';
const MAC_APP_STORE_LINK = 'https://apps.apple.com/us/app/mchatai/id6757826624?mt=12';

// What a tour page is open on: 'android', 'ios' or 'desktop'. iPadOS Safari
// asks for desktop sites and says "Macintosh", so a Mac with a touch screen is
// an iPad (the Seattle Landmarks walks page tells them apart the same way).
export function tourPlatform(userAgent = '', maxTouchPoints = 0) {
  if (/Android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) return 'ios';
  return 'desktop';
}

// "Open in mChatAI" for a tour. iPhone takes the mchatai:// scheme; the macOS
// app takes com.sevenhillsstudio.mchatai://. Android gets an intent:// URL, so
// Chrome opens the app when it is installed and goes to Google Play when it is
// not; a bare custom scheme does nothing when the app is missing. Same link
// shape as the Seattle Landmarks walks page.
export function tourInstallURL(productId, userAgent = '', maxTouchPoints = 0) {
  const path = `community/install?id=${encodeURIComponent(productId)}`;
  const platform = tourPlatform(userAgent, maxTouchPoints);
  if (platform === 'android') {
    return `intent://${path}#Intent;scheme=mchatai;package=${ANDROID_PACKAGE};`
      + `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_LINK)};end`;
  }
  if (platform === 'ios') return `mchatai://${path}`;
  return `com.sevenhillsstudio.mchatai://${path}`;
}

// The web guides whose walks are also published to the Hub. A guide lists its
// walks in resources/tours.json, each with the Hub product it publishes into
// (`p`), so a walk's web version is looked up from the guide, never typed here.
const WEB_GUIDES = ['/seattle-landmarks/'];

export function webWalkURL(tours, guideRoot, productId) {
  const walk = Array.isArray(tours) ? tours.find((t) => t && t.id && t.p === productId) : null;
  return walk ? `${guideRoot}#/w/${walk.id}` : '';
}

// '' until a guide answers, and for any walk no guide carries.
function useWebWalkURL(productId) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!productId) return undefined;
    let live = true;
    (async () => {
      for (const root of WEB_GUIDES) {
        try {
          const r = await fetch(`${root}resources/tours.json`);
          if (!r.ok) continue;
          const found = webWalkURL(await r.json(), root, productId);
          if (found) {
            if (live) setUrl(found);
            return;
          }
        } catch (e) {
          // A guide that can't be read only hides the web option.
        }
      }
    })();
    return () => { live = false; };
  }, [productId]);
  return url;
}

// How someone takes a walk (2026-10-04). Most people who land here have never
// heard of mChatAI, so the page says what each way needs: the web version
// needs nothing, the app needs installing first. A phone sees the app first,
// since walking it is the point. A computer sees the web version first, and a
// code that moves this page to a phone; qrcode loads only then.
//
// A tap on Open shows a line under it that goes when the app comes forward
// (a phone hides the page, a computer moves focus to the app). Never a timed
// jump to a store: Safari shows its "Open in mChatAI?" prompt and its "address
// is invalid" alert over the same visible page, and a jump would cancel the
// prompt for people who have the app.
function TourWays({ productId, pageURL, webURL, platform }) {
  const phone = platform !== 'desktop';
  const mac = !phone && /Macintosh/.test(navigator.userAgent);
  const [openTapped, setOpenTapped] = useState(false);
  const [qr, setQr] = useState('');

  useEffect(() => {
    if (!openTapped) return undefined;
    const appCameForward = () => { if (document.hidden || !document.hasFocus()) setOpenTapped(false); };
    document.addEventListener('visibilitychange', appCameForward);
    window.addEventListener('blur', appCameForward);
    return () => {
      document.removeEventListener('visibilitychange', appCameForward);
      window.removeEventListener('blur', appCameForward);
    };
  }, [openTapped]);

  useEffect(() => {
    if (phone) return undefined;
    let live = true;
    import('qrcode')
      .then((QR) => QR.toDataURL(pageURL, { margin: 1, width: 264 }))
      .then((data) => { if (live) setQr(data); })
      .catch(() => {});
    return () => { live = false; };
  }, [phone, pageURL]);

  const open = () => {
    window.location.href = tourInstallURL(productId, navigator.userAgent, navigator.maxTouchPoints || 0);
    // Android needs no line: Chrome goes to Google Play when the app is missing.
    if (platform !== 'android') setOpenTapped(true);
  };

  const web = webURL && (
    <div className="hp-way">
      <h3>Read it on the web</h3>
      <p>
        Every stop with its history and sources, on any phone or computer.
        Press play on a stop to hear it read aloud.
      </p>
      <a className={`hp-btn${phone ? '' : ' hp-btn--primary'}`} href={webURL}>Open the web version</a>
    </div>
  );

  const app = (
    <div className="hp-way">
      <h3>Walk it with the free mChatAI app</h3>
      <p>The walk opens in the app's Map Guide, with its stops on a map and each one read aloud.</p>
      {phone ? (
        <ol className="hp-way-steps">
          <li>
            Get mChatAI from{' '}
            {platform === 'android'
              ? <a href={PLAY_STORE_LINK}>Google Play</a>
              : <a href={APP_STORE_LINK}>the App Store</a>}. It's free.
          </li>
          <li>
            Come back to this page and tap Open in mChatAI.
            <div className="hp-way-action">
              <button type="button" className="hp-btn hp-btn--primary" onClick={open}>Open in mChatAI</button>
            </div>
          </li>
        </ol>
      ) : (
        <div className="hp-way-phone">
          {qr && <img className="hp-info-qr" src={qr} width="132" height="132" alt="QR code that opens this page on a phone" />}
          <p>
            Scan the code with your phone's camera to open this page there.
            mChatAI is free on the <a href={APP_STORE_LINK}>App Store</a> (iPhone)
            and <a href={PLAY_STORE_LINK}>Google Play</a> (Android).
          </p>
        </div>
      )}
      {openTapped && platform === 'ios' && (
        <p className="hp-info-apphint">Nothing opened? Get mChatAI first (step 1), then tap Open in mChatAI again.</p>
      )}
      {mac && (
        <p className="hp-way-mac">
          Have mChatAI+ on this Mac?{' '}
          <button type="button" className="hp-linkbtn" onClick={open}>Open the walk there</button>.
          {' '}If not, it's free on the <a href={MAC_APP_STORE_LINK}>Mac App Store</a>.
        </p>
      )}
      {openTapped && mac && <p className="hp-info-apphint">Nothing opened? This Mac doesn't have mChatAI+.</p>}
    </div>
  );

  return (
    <section className="hp-ways">
      <h2>How to take this walk</h2>
      <div className="hp-ways-grid">{phone ? <>{app}{web}</> : <>{web}{app}</>}</div>
    </section>
  );
}

// TrustBadge removed 2026-04-15: Phase RT.2 unified Discovery dropped trust-
// tier distinctions (Official / Verified / Community). The seed "Official"
// catalog was LARPing as curated content while actually 404-ing on click.
// All products now share one pool with no badge hierarchy. Re-introduce this
// component if a real moderation tier surfaces later.

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function coerceDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'number') {
    const date = new Date(value > 100000000000 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function formatProductDate(value) {
  const date = coerceDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

function addUnique(list, value) {
  if (!value || typeof value !== 'string') return;
  const trimmed = value.trim();
  if (!trimmed) return;
  if (trimmed.length > 36 || /^https?:\/\//i.test(trimmed) || /^[~^]?\d+(\.\d+){0,3}/.test(trimmed)) return;
  if (!list.some((item) => item.toLowerCase() === trimmed.toLowerCase())) list.push(trimmed);
}

function collectStrings(value, out = []) {
  if (!value) return out;
  if (typeof value === 'string') {
    out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectStrings(item, out));
  } else if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectStrings(item, out));
  }
  return out;
}

function inferTechStack(product) {
  const found = [];
  const directKeys = ['frameworks', 'libraries', 'runtimeLibraries', 'techStack', 'technology', 'dependencies'];
  directKeys.forEach((key) => collectStrings(product[key]).forEach((value) => addUnique(found, value)));
  directKeys.forEach((key) => collectStrings(product.sourceBundle?.[key]).forEach((value) => addUnique(found, value)));

  const text = collectStrings([
    product.name,
    product.summary,
    product.description,
    product.generatorName,
    product.modelHint,
    product.productType,
    product.contentCategory,
    product.type,
    product.subType,
    product.tags,
    product.topics,
    product.sourceBundle?.sourceArtifactID,
    product.sourceBundle?.componentIDs,
    product.sourceBundle?.wisdomIDs,
  ]).join(' ').toLowerCase();

  if (/three\.?js|\bthree\b/.test(text)) addUnique(found, 'Three.js');
  if (/webgl/.test(text)) addUnique(found, 'WebGL');
  if (/react/.test(text)) addUnique(found, 'React');
  if (/web components|mchatai-web-components/.test(text)) addUnique(found, 'Web Components');
  if (/canvas/.test(text)) addUnique(found, 'HTML5 Canvas');
  if (/miniapp|mini-app/.test(text) || product.productType === 'com.mchatai.product.web') addUnique(found, 'Web Mini App');
  if (/game/.test(text)) addUnique(found, 'Browser Game');
  if (/swiftui/.test(text)) addUnique(found, 'SwiftUI');
  if (/spritekit/.test(text)) addUnique(found, 'SpriteKit');
  if (/scenekit/.test(text)) addUnique(found, 'SceneKit');
  if (/mapkit/.test(text)) addUnique(found, 'MapKit');

  return found.slice(0, 4).join(', ');
}

function findGenerationPrompt(product) {
  const candidates = [
    product.originalGoal,
    product.goal,
    product.userGoal,
    product.prompt,
    product.userPrompt,
    product.generationPrompt,
    product.sourceBundle?.originalGoal,
    product.sourceBundle?.goal,
    product.sourceBundle?.userGoal,
    product.sourceBundle?.prompt,
    product.sourceBundle?.userPrompt,
    product.sourceBundle?.generationPrompt,
    product.sourceBundle?.buildPrompt,
  ];
  const match = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return match ? match.trim() : '';
}

function humanizeToken(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function productFocusSummary(product) {
  const generic = new Set([
    'miniapp', 'mini app', 'game', 'games', 'website', 'web', 'prompt',
    'media', 'tour', 'mchatai',
    String(product.contentCategory || '').toLowerCase(),
    String(product.type || '').toLowerCase(),
  ]);
  const values = [...(product.topics || []), ...(product.tags || [])]
    .map((value) => String(value || '').trim())
    .filter((value) => value && !generic.has(value.toLowerCase()));
  const unique = [];
  values.forEach((value) => {
    if (!unique.some((item) => item.toLowerCase() === value.toLowerCase())) unique.push(value);
  });
  return unique.slice(0, 3).map(humanizeToken).join(', ');
}

function fileNameFromURL(url) {
  try {
    const parsed = new URL(url);
    const decodedPath = decodeURIComponent(parsed.pathname);
    return decodedPath.split('/').filter(Boolean).pop() || url;
  } catch (_) {
    return String(url || '').split('/').pop() || String(url || '');
  }
}

function curatedArtifactLink(url) {
  const fileName = fileNameFromURL(url);
  const lower = fileName.toLowerCase();
  const labels = {
    'miniapp.md': 'Mini App Manifest',
    'claude.context.md': 'Harness Context Notes',
    'claude.md': 'Agent Instructions',
    'output.md': 'Build Output Notes',
    'output.claude.md': 'Claude Output Notes',
    'output.codex.md': 'Codex Output Notes',
  };
  const label = labels[lower];
  return label ? { href: url, label } : null;
}

// The build materials a signed-in reader can open (RemixKitPanel).
function buildMaterialLinks(sourceBundle) {
  const bundle = sourceBundle || {};
  const artifactURLs = Array.isArray(bundle.artifactURLs) ? bundle.artifactURLs : [];
  return [
    bundle.sourceArchiveURL && { href: bundle.sourceArchiveURL, label: 'Download Source Archive' },
    bundle.buildTranscriptURL && { href: bundle.buildTranscriptURL, label: 'Build Transcript' },
    ...artifactURLs.map(curatedArtifactLink),
  ].filter(Boolean);
}

function sourceContextURL(item) {
  const id = String(item.id || '').trim();
  if (!id || !/^[a-z0-9._@/-]+$/i.test(id)) return '';
  const base = 'https://github.com/LawrenceNorman/mchatai-source/blob/main/';
  if (item.kind === 'Wisdom') {
    return `${base}wisdom/packs/${encodeURIComponent(id)}.json`;
  }
  return '';
}

function ScreenshotCarousel({ urls }) {
  const [active, setActive] = useState(0);
  if (!urls || urls.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000', aspectRatio: '16/9', width: '100%' }}>
        <img
          src={urls[active]}
          alt={`Screenshot ${active + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        {urls.length > 1 && (
          <>
            <button
              onClick={() => setActive((a) => (a - 1 + urls.length) % urls.length)}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: '1rem' }}
            >‹</button>
            <button
              onClick={() => setActive((a) => (a + 1) % urls.length)}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: '1rem' }}
            >›</button>
          </>
        )}
      </div>
      {urls.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, overflowX: 'auto' }}>
          {urls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Thumbnail ${i + 1}`}
              onClick={() => setActive(i)}
              style={{
                width: 72, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                border: i === active ? '2px solid #3b82f6' : '2px solid transparent', flexShrink: 0
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RemixKitPanel({ product, generationPrompt, hidePrompt = false }) {
  const sourceBundle = product.sourceBundle || {};
  const sourceLinks = buildMaterialLinks(sourceBundle);

  const sourceContext = [
    ...(sourceBundle.wisdomIDs || []).map((id) => ({ kind: 'Wisdom', id })),
    ...(sourceBundle.templateIDs || []).map((id) => ({ kind: 'Template', id })),
    ...(sourceBundle.assetPackIDs || []).map((id) => ({ kind: 'Asset Pack', id })),
    ...(sourceBundle.capabilityPackIDs || []).map((id) => ({ kind: 'Capability Pack', id })),
    ...(sourceBundle.componentIDs || []).map((id) => ({ kind: 'Component', id })),
  ].filter((item) => item.id);

  const artifactID = sourceBundle.sourceArtifactID;
  const artifactIDIsSlug = artifactID && [product.slug, product.id, product.packageId].filter(Boolean).includes(artifactID);
  const harnessTurnCount = Number.isFinite(Number(sourceBundle.harnessTurnCount)) ? Number(sourceBundle.harnessTurnCount) : null;
  const runFacts = [
    // Ledger-identity (2026-07-05) — which coding-agent CLI + model built this,
    // stamped by the publish pipeline from the artifact's MINIAPP.md.
    sourceBundle.cliBackend && { label: 'CLI Backend', value: sourceBundle.cliBackend },
    sourceBundle.cliModel && { label: 'Model', value: sourceBundle.cliModel },
    harnessTurnCount && { label: 'Harness Turns', value: harnessTurnCount },
    sourceBundle.selectedRecipeID && { label: 'Recipe', value: sourceBundle.selectedRecipeID },
    sourceBundle.sourceScaffoldID && { label: 'Scaffold', value: sourceBundle.sourceScaffoldID },
  ].filter(Boolean);
  const fingerprints = [
    !artifactIDIsSlug && artifactID && { label: 'Artifact', value: artifactID },
    sourceBundle.referenceBuildID && { label: 'Reference Build', value: sourceBundle.referenceBuildID },
    sourceBundle.generatorRunUUID && { label: 'Generator Run', value: sourceBundle.generatorRunUUID },
    sourceBundle.productDNAUUID && { label: 'Product DNA', value: sourceBundle.productDNAUUID },
    sourceBundle.wizardSessionID && { label: 'Wizard Session', value: sourceBundle.wizardSessionID },
  ].filter(Boolean);

  const missing = [
    !generationPrompt && 'original prompt/goal',
    !harnessTurnCount && 'Harness turn count',
    !sourceBundle.buildTranscriptURL && 'build transcript',
    sourceContext.length === 0 && 'mchatai-source wisdom/template/component IDs',
  ].filter(Boolean);

  const sectionStyle = {
    borderTop: '1px solid #e2e8f0',
    paddingTop: 14,
    marginTop: 14,
  };
  const linkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '7px 10px',
    borderRadius: 999,
    border: '1px solid #cbd5e1',
    color: '#1d4ed8',
    background: '#fff',
    fontSize: '0.82rem',
    fontWeight: 800,
    textDecoration: 'none',
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, marginBottom: 28 }}>
      <div style={{ color: '#0f172a', fontWeight: 800, marginBottom: 8 }}>Remix Kit</div>
      <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
        Curated source materials from this publish. The archive contains the full file tree; this panel only calls out materials that help explain how the product was assembled or branched.
      </p>

      {generationPrompt && !hidePrompt && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 6 }}>Original Prompt / Goal</div>
          <p style={{ color: '#475569', fontSize: '0.86rem', lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap' }}>
            {generationPrompt}
          </p>
        </div>
      )}

      {sourceLinks.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>Build Materials</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {sourceLinks.map((link) => (
              <a key={`${link.label}-${link.href}`} href={link.href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {runFacts.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>Run Facts</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {runFacts.map((item) => (
              <div key={item.label}>
                <div style={{ color: '#94a3b8', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{item.label}</div>
                <div style={{ color: '#334155', fontSize: '0.82rem', fontWeight: 800, overflowWrap: 'anywhere' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sourceContext.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 6 }}>mChatAI Source Context</div>
          <p style={{ color: '#475569', fontSize: '0.84rem', lineHeight: 1.5, margin: '0 0 8px' }}>
            Wisdom packs, templates, assets, or components recorded by the publish pipeline.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {sourceContext.map((item) => {
              const href = sourceContextURL(item);
              const label = `${item.kind}: ${item.id}`;
              return href ? (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  {label}
                </a>
              ) : (
                <span key={label} style={{ ...linkStyle, color: '#334155', background: '#f8fafc', cursor: 'default' }}>
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {Array.isArray(sourceBundle.externalReferences) && sourceBundle.externalReferences.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>External References</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {sourceBundle.externalReferences.map((reference) => (
              <div key={reference.url}>
                <a href={reference.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 800, textDecoration: 'none' }}>
                  {reference.title}
                </a>
                {reference.reviewStatus && (
                  <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'capitalize', marginLeft: 8 }}>
                    {reference.reviewStatus}
                  </span>
                )}
                {reference.summary && (
                  <p style={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.5, margin: '4px 0 0' }}>
                    {reference.summary}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {fingerprints.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>Build Fingerprints</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
            {fingerprints.map((item) => (
              <div key={item.label}>
                <div style={{ color: '#94a3b8', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{item.label}</div>
                <div style={{ color: '#334155', fontSize: '0.8rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', overflowWrap: 'anywhere' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {missing.length > 0 && (
        <p style={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.45, margin: '14px 0 0' }}>
          Still missing from this published record: {missing.join(', ')}.
        </p>
      )}

      {/* Phase HUB-GATE (2026-07-05) — the Remix Kit is the strongest proof
          of what the platform does, so it carries the download funnel even
          for signed-in readers. */}
      <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.5 }}>
          Every mChatAI+ build produces a kit like this — prompt in, playable mini-app out, materials included.
        </span>
        <Link to="/macos-app" style={{ ...linkStyle, background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' }}>
          Get mChatAI+ for Mac
        </Link>
      </div>
    </div>
  );
}

// Phase HUB-GATE (2026-07-05) — the signed-out teaser used to be one generic
// "sign in to inspect" sentence. Itemizing the actual locked materials (labels
// only — the URLs stay behind the gate) shows visitors what an account
// unlocks, and the second CTA turns the panel into the mChatAI+ download
// funnel for people who want to build their own.
// Stock lines the publish flows write when nobody wrote a description. They
// describe the pipeline, not the product, so the page leaves them out rather
// than repeat "A mini app built with mChatAI." on 150 products.
const STOCK_LINES = [
  /^built with the mchatai aiharness\b/i,
  /^generated via mchatai\+? ai automation\b/i,
  /^an? (mini app|utility|game|media|macos app) built with mchatai\.?$/i,
];

// The product's own description, or '' when all it has is its name or a stock line.
export function humanDescription(product) {
  const name = String(product?.name || '').trim().toLowerCase();
  const found = [product?.description, product?.summary]
    .map((text) => String(text || '').trim())
    .find((text) => text.length >= 12
      && text.toLowerCase() !== name
      && !STOCK_LINES.some((pattern) => pattern.test(text)));
  return found || '';
}

// Final end-products (images, audio, video, PDFs, prompts) never iterate —
// no version, no "made with" tooling, no build record.
const FINAL_PRODUCT_CATEGORIES = new Set(['media', 'prompt', 'image', 'audio', 'video', 'pdf']);

function formatTourDistance(meters) {
  if (!meters || meters <= 0) return '';
  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

function formatTourDuration(minutes) {
  if (!minutes || minutes <= 0) return '';
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
  }
  return `${minutes} min`;
}

function generatorFor(product) {
  if (product.generatorName) return product.generatorName;
  const category = (product.contentCategory || '').toLowerCase();
  const sub = (product.subType || '').toLowerCase();
  if (category === 'miniapp' || (product.productType || '').toLowerCase() === 'com.mchatai.product.web') return 'AIHarness (web app builder)';
  if (category === 'game' || sub === 'game') return 'GameMaker Agent';
  if (sub === 'pipeline') return 'Pipeline Architect';
  if (sub === 'agent') return 'Agent Studio';
  return 'mChatAI';
}

// The thing itself, for a product that doesn't run in a browser: a walk's
// cover, a prompt's image, a track's audio.
function ProductHero({ product }) {
  const category = (product.contentCategory || product.type || '').toLowerCase();
  const subType = product.subType || '';
  const shots = Array.isArray(product.screenshotURLs) ? product.screenshotURLs.filter(Boolean) : [];
  const image = shots.length === 0 && category === 'prompt' && subType === 'dreamsnap' ? product.iconURL : null;
  return (
    <>
      {shots.length > 0 && <ScreenshotCarousel urls={shots} />}
      {image && (
        <div className="hp-info-hero">
          <img src={image} alt={product.name} />
        </div>
      )}
      {product.videoPreviewURL && shots.length === 0 && (
        <div className="hp-info-hero">
          <video controls preload="metadata" src={product.videoPreviewURL} playsInline />
        </div>
      )}
      {product.audioPreviewURL && (
        <div className="hp-info-audio">
          <audio controls preload="metadata" src={product.audioPreviewURL} />
        </div>
      )}
    </>
  );
}

export function ProductInfo({ product, slug, variant = 'page', onProductChange }) {
  const { user } = useAuth();
  const isSignedIn = !!user && !user.isAnonymous;
  const [shareToast, setShareToast] = useState(false);
  const [myRating, setMyRating] = useState(null);
  const [rateStatus, setRateStatus] = useState(''); // 'submitting' | 'saved' | 'signInRequired' | 'error'
  const [promptOpen, setPromptOpen] = useState(false);
  const productId = product.id || product.packageId;

  useEffect(() => {
    if (!isSignedIn || !productId) return;
    fetchMyRating(productId).then((rating) => setMyRating(rating));
  }, [isSignedIn, productId]);

  const category = (product.contentCategory || product.type || '').toLowerCase();
  const isTour = category === 'tour';
  const platform = isTour ? tourPlatform(navigator.userAgent, navigator.maxTouchPoints || 0) : '';
  const webWalk = useWebWalkURL(isTour ? productId : '');
  const isMacOSApp = category === 'macosapp';
  const isFinal = FINAL_PRODUCT_CATEGORIES.has(category);
  const isAudioArtifact = !!product.audioPreviewURL
    || (category === 'media' && /^(audio|podcast)/.test((product.subType || '').toLowerCase()))
    || (product.productType || '').toLowerCase().includes('podcast');
  const description = humanDescription(product);
  const publisherUid = product.publisherUid || product.publisher?.uid || '';
  const publisherShort = publisherUid ? publisherUid.slice(0, 8) : '';
  const publisherName = product.publisherName || product.publisher?.displayName || '';
  const updated = formatProductDate(product.updatedAt || product.publishedAt || product.createdAt || product.createdAtMs);
  const generationPrompt = findGenerationPrompt(product);
  const showBuild = !isFinal && !isAudioArtifact && (product.sourceBundle || generationPrompt);
  const tourFacts = isTour ? [
    product.tourStops != null && `${product.tourStops} stops`,
    formatTourDistance(product.tourDistanceMeters),
    formatTourDuration(product.tourDurationMinutes),
    product.tourCityHint,
  ].filter(Boolean).join(' · ') : '';
  const feedbackHref = `/feedback?productId=${encodeURIComponent(productId || '')}`
    + `&slug=${encodeURIComponent(product.slug || slug)}&name=${encodeURIComponent(product.name || '')}`
    + `&preview=${encodeURIComponent(product.previewURL || '')}&src=web.hubInfo`;

  const details = [
    !isFinal && { label: 'Made with', value: generatorFor(product) },
    !isFinal && { label: 'Version', value: product.version || product.latestVersion || '1.0.0' },
    { label: 'Published', value: formatProductDate(product.publishedAt || product.createdAt || product.createdAtMs) },
    { label: 'Topics', value: productFocusSummary(product) },
    { label: 'Built with', value: inferTechStack(product) },
    { label: 'Size', value: formatBytes(product.bundleSizeBytes) },
    Array.isArray(product.permissions) && product.permissions.length > 0 && {
      label: 'Permissions',
      value: product.permissions.map((permission) => (typeof permission === 'string' ? permission : permission.id)).join(', '),
    },
    { label: 'Product ID', value: productId, mono: true },
  ].filter((item) => item && item.value);

  async function handleShare() {
    // The product's own page, never a /play link: that verb is gone.
    const url = new URL(`/hub/${encodeURIComponent(product.slug || slug)}`, window.location.origin).toString();
    if (navigator.share) {
      try { await navigator.share({ title: product.name || 'mChatAI', url }); return; }
      catch (_) { /* cancelled; fall through to copying */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 1800);
    } catch (_) { /* clipboard denied */ }
  }

  async function handleRate(rating) {
    if (!isSignedIn) { setRateStatus('signInRequired'); return; }
    if (rateStatus === 'submitting') return;
    setRateStatus('submitting');
    const response = await submitRating(productId, rating);
    if (!response.ok) {
      setRateStatus(response.error === 'signInRequired' ? 'signInRequired' : 'error');
      return;
    }
    // The server keeps one rating per user per product. Update the local
    // average; a changed rating replaces the old contribution.
    if (onProductChange) {
      onProductChange((current) => {
        const oldCount = current.ratingCount || 0;
        const oldSum = (current.averageRating || 0) * oldCount;
        const hadPrior = myRating != null;
        const newCount = hadPrior ? oldCount : oldCount + 1;
        const newSum = hadPrior ? (oldSum - myRating + rating) : (oldSum + rating);
        return { ...current, ratingCount: newCount, averageRating: Number((newSum / Math.max(newCount, 1)).toFixed(2)) };
      });
    }
    setMyRating(rating);
    setRateStatus('saved');
  }

  return (
    <div className={`hp-info hp-info--${variant}`}>
      <header className="hp-info-head">
        {product.iconURL && <img className="hp-info-icon" src={product.iconURL} alt="" />}
        <div className="hp-info-headtext">
          <h1 className="hp-info-title">{product.name}</h1>
          <div className="hp-info-byline">
            {publisherName && (
              <>
                by{' '}
                {publisherShort
                  ? <Link to={`/user/${publisherShort}`}>{publisherName}</Link>
                  : <span>{publisherName}</span>}
              </>
            )}
            {updated && <span>{publisherName ? ' · ' : ''}Updated {updated}</span>}
          </div>
        </div>
      </header>

      {tourFacts && <p className="hp-info-facts">{tourFacts}</p>}
      {variant === 'page' && <ProductHero product={product} />}
      {description && <p className={`hp-info-desc${isTour ? ' hp-info-desc--pre' : ''}`}>{description}</p>}
      {product.publisherComment && (
        <blockquote className="hp-info-note">
          <span className="hp-info-note-label">From the author</span>
          {product.publisherComment}
        </blockquote>
      )}

      {isTour && (
        <TourWays
          productId={productId}
          pageURL={`https://mchatai.com/hub/${product.slug || slug}`}
          webURL={webWalk}
          platform={platform}
        />
      )}

      <div className="hp-info-actions">
        {isMacOSApp && product.bundleURL && (
          <a
            className="hp-btn hp-btn--primary"
            href={product.bundleURL}
            download
            title={product.hasCompiledBundle
              ? 'The ready-to-run macOS app. Unzip it and double-click to launch.'
              : 'The Swift Package source. Open the unzipped folder in Xcode, or run `swift run` in it.'}
          >
            {product.hasCompiledBundle ? 'Download the app' : 'Download the source'}
            {product.bundleSizeBytes > 0 ? ` · ${formatBytes(product.bundleSizeBytes)}` : ''}
          </a>
        )}
        <button type="button" className="hp-btn" onClick={handleShare}>
          <ShareIcon width="15" height="15" /> {shareToast ? 'Link copied' : 'Share'}
        </button>
        <Link className="hp-btn" to={feedbackHref}>Feedback</Link>
      </div>

      {showBuild && (
        <section className="hp-info-section">
          <h2>How it was made</h2>
          {!isSignedIn && (
            <>
              <p>
                Built with mChatAI+. The author shared the build record
                {generationPrompt ? ', including the prompt that started it' : ''}.
              </p>
              <div className="hp-info-actions">
                <Link className="hp-btn hp-btn--primary" to="/login">Sign in to see it</Link>
                <Link className="hp-btn" to="/macos-app">Get mChatAI+</Link>
              </div>
            </>
          )}
          {isSignedIn && (
            <>
              {generationPrompt && (
                <>
                  <p>Built with mChatAI+ from this prompt:</p>
                  <p className={`hp-info-prompt${promptOpen ? ' hp-info-prompt--open' : ''}`}>{generationPrompt}</p>
                  {generationPrompt.length > 280 && (
                    <button type="button" className="hp-linkbtn" onClick={() => setPromptOpen((open) => !open)}>
                      {promptOpen ? 'Show less' : 'Show all'}
                    </button>
                  )}
                </>
              )}
              {!generationPrompt && <p>Built with mChatAI+.</p>}
              {product.sourceBundle && (
                <details className="hp-info-details">
                  <summary>Full build record</summary>
                  <RemixKitPanel product={product} generationPrompt={generationPrompt} hidePrompt />
                </details>
              )}
            </>
          )}
        </section>
      )}

      <section className="hp-info-section">
        <RatingStars
          myRating={myRating}
          isSignedIn={isSignedIn}
          rateStatus={rateStatus}
          averageRating={product.averageRating || 0}
          ratingCount={product.ratingCount || 0}
          onRate={handleRate}
        />
      </section>

      {details.length > 0 && (
        <details className="hp-info-details">
          <summary>Technical details</summary>
          <dl className="hp-info-dl">
            {details.map((item) => (
              <React.Fragment key={item.label}>
                <dt>{item.label}</dt>
                <dd className={item.mono ? 'hp-mono' : ''}>{item.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}

export default ProductInfo;

// Phase HUB-REDESIGN-2 — tappable star rating row that lives under the
// artifact. Replaces the sidebar Rate panel (1-5 numbered buttons). Glyph
// + hover behavior matches the web FeedbackPage stars so the user sees
// the same affordance across surfaces.
function RatingStars({ myRating, isSignedIn, rateStatus, averageRating, ratingCount, onRate }) {
  const [hover, setHover] = useState(0);
  const visible = hover || myRating || 0;

  return (
    <div style={{
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: 18,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, opacity: isSignedIn ? 1 : 0.55 }}>
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= visible;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onRate(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                disabled={!isSignedIn || rateStatus === 'submitting'}
                aria-label={`Rate ${n} star${n === 1 ? '' : 's'}`}
                style={{
                  background: 'none', border: 'none',
                  cursor: isSignedIn ? 'pointer' : 'not-allowed',
                  padding: 4, fontSize: '1.75rem', lineHeight: 1,
                  color: filled ? '#f59e0b' : '#d1d5db',
                  transform: filled ? 'scale(1.08)' : 'scale(1)',
                  transition: 'color 0.12s, transform 0.12s',
                }}
              >
                &#9733;
              </button>
            );
          })}
        </div>
        {ratingCount > 0 && (
          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            {averageRating.toFixed(1)} / 5 &middot; {ratingCount} rating{ratingCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {!isSignedIn && (
        <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>Sign in to rate.</span>
      )}
      {isSignedIn && rateStatus === 'saved' && myRating != null && (
        <span style={{ color: '#059669', fontSize: '0.78rem' }}>✓ Your rating: {myRating} ★</span>
      )}
      {isSignedIn && myRating != null && rateStatus !== 'saved' && (
        <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>Your rating: {myRating} ★</span>
      )}
      {rateStatus === 'error' && (
        <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>Couldn't save your rating. Try again in a moment.</span>
      )}
    </div>
  );
}
